import React from 'react'
import { GridColDef, GridRenderCellParams } from '@mui/x-data-grid-pro'
import tw from 'twin.macro'
import { Typography } from '@mui/material'
import { PartnerInvoiceActionsCell } from 'src/components/invoice/grid/PartnerInvoiceActionsCell'
import { PartnerInvoiceStatusCell } from 'src/components/invoice/grid/PartnerInvoiceStatusCell'
import type { PartnerInvoice, PendingInvoiceAction } from 'src/interfaces/invoice'
import { formatDateOnly, parseIsoDate } from 'src/utils/grid'
import { getPartnerInvoiceLabel } from 'src/utils/invoiceLabels'
import { formatCentsAsUsd, formatRate } from 'src/utils/invoiceMoney'
import { partnerInvoiceTableHeaderTitles, partnerInvoiceTypeLabels } from './constants'
import { PartnerInvoicesTableFields } from './enums'

/**
 * Every field Notion lists is present. These twenty-odd start hidden and are restorable from
 * the manage-columns panel — the nine that remain visible are the ones an admin reads first.
 * Nothing is dropped; the status chip summarises, it does not replace.
 */
export const PARTNER_INVOICE_DEFAULT_HIDDEN_FIELDS: PartnerInvoicesTableFields[] = [
  PartnerInvoicesTableFields.externalStatus,
  PartnerInvoicesTableFields.loanFundingStatus,
  PartnerInvoicesTableFields.loanFundingErrorMessage,
  PartnerInvoicesTableFields.paymentStatus,
  PartnerInvoicesTableFields.paymentErrorMessage,
  PartnerInvoicesTableFields.paymentDate,
  PartnerInvoicesTableFields.payInvoiceOnDate,
  PartnerInvoicesTableFields.loanId,
  PartnerInvoicesTableFields.loanDueDate,
  PartnerInvoicesTableFields.loanTotalAmount,
  PartnerInvoicesTableFields.loanBaseFeeAmount,
  PartnerInvoicesTableFields.loanTransferFee,
  PartnerInvoicesTableFields.loanTransferRate,
  PartnerInvoicesTableFields.wireTransferFee,
  PartnerInvoicesTableFields.wireTransferRate,
  PartnerInvoicesTableFields.gigsafeTotalFeeAmount,
  PartnerInvoicesTableFields.invoiceApprovedByPayor,
  PartnerInvoicesTableFields.payorExternalId,
  PartnerInvoicesTableFields.payeeExternalId,
  PartnerInvoicesTableFields.paymentId,
  PartnerInvoicesTableFields.description,
]

const MISSING_VALUE = '—'

/** Shared renderer for the plain optional string columns, so a null never renders as blank. */
const renderOptionalText = (value: string | undefined) => {
  if (value === undefined || value === '') {
    return (
      <Typography variant="body2" css={tw`text-shades-500`}>
        {MISSING_VALUE}
      </Typography>
    )
  }

  return (
    <Typography variant="body2" css={tw`truncate`}>
      {value}
    </Typography>
  )
}

const buildTextColumn = (
  field: PartnerInvoicesTableFields,
  getValue: (invoice: PartnerInvoice) => string | undefined,
  width = 160,
): GridColDef<PartnerInvoice> => {
  return {
    field,
    headerName: partnerInvoiceTableHeaderTitles[field],
    width,
    // Client-side sorting is correct HERE: the detail panel holds every nested row already, so
    // a sort reorders a complete set and cannot misrepresent anything.
    sortable: true,
    display: 'flex',
    renderCell: (params: GridRenderCellParams<PartnerInvoice>) => {
      return renderOptionalText(getValue(params.row))
    },
  }
}

const buildMoneyColumn = (
  field: PartnerInvoicesTableFields,
  getValue: (invoice: PartnerInvoice) => number | undefined,
): GridColDef<PartnerInvoice> => {
  return {
    field,
    headerName: partnerInvoiceTableHeaderTitles[field],
    width: 140,
    sortable: true,
    align: 'right',
    headerAlign: 'right',
    display: 'flex',
    renderCell: (params: GridRenderCellParams<PartnerInvoice>) => {
      // Cents → dollars. Never render one of these raw.
      return <Typography variant="body2">{formatCentsAsUsd(getValue(params.row))}</Typography>
    },
  }
}

const buildRateColumn = (
  field: PartnerInvoicesTableFields,
  getValue: (invoice: PartnerInvoice) => number | undefined,
): GridColDef<PartnerInvoice> => {
  return {
    field,
    headerName: partnerInvoiceTableHeaderTitles[field],
    width: 140,
    sortable: true,
    align: 'right',
    headerAlign: 'right',
    display: 'flex',
    renderCell: (params: GridRenderCellParams<PartnerInvoice>) => {
      // A decimal percent, NOT cents — `formatRate`, never `formatCentsAsUsd`.
      return <Typography variant="body2">{formatRate(getValue(params.row))}</Typography>
    },
  }
}

const buildDateColumn = (
  field: PartnerInvoicesTableFields,
  valueField: keyof PartnerInvoice,
): GridColDef<PartnerInvoice> => {
  return {
    field,
    headerName: partnerInvoiceTableHeaderTitles[field],
    width: 150,
    sortable: true,
    type: 'date',
    // These are ISO `timestamptz` STRINGS. `parseUnixTimestamp` would yield NaN here — it is
    // for the unix-seconds `createdAt`/`updatedAt` every other slice in this app returns.
    valueGetter: (_value, row: PartnerInvoice) => {
      return parseIsoDate(row[valueField])
    },
    valueFormatter: formatDateOnly,
  }
}

export const buildPartnerInvoiceColumns = (
  composedInvoiceLabel: string,
  onRunAction: (action: PendingInvoiceAction) => void,
  // `invoices_manage` (ENG-1207 part 2): every verb in the menu posts to an invoices_manage route,
  // so without it the actions column is dropped.
  canManage: boolean,
): Array<GridColDef<PartnerInvoice>> => {
  const columns: Array<GridColDef<PartnerInvoice>> = [
    {
      field: PartnerInvoicesTableFields.invoice,
      headerName: partnerInvoiceTableHeaderTitles[PartnerInvoicesTableFields.invoice],
      flex: 1,
      minWidth: 200,
      sortable: true,
      hideable: false,
      display: 'flex',
      valueGetter: (_value, row: PartnerInvoice) => {
        return getPartnerInvoiceLabel(row)
      },
      renderCell: (params: GridRenderCellParams<PartnerInvoice>) => {
        return (
          <Typography variant="body2" css={tw`truncate`}>
            {getPartnerInvoiceLabel(params.row)}
          </Typography>
        )
      },
    },
    {
      field: PartnerInvoicesTableFields.status,
      headerName: partnerInvoiceTableHeaderTitles[PartnerInvoicesTableFields.status],
      width: 200,
      sortable: true,
      display: 'flex',
      renderCell: (params: GridRenderCellParams<PartnerInvoice>) => {
        return <PartnerInvoiceStatusCell invoice={params.row} />
      },
    },
    buildTextColumn(
      PartnerInvoicesTableFields.type,
      (invoice) => {
        return partnerInvoiceTypeLabels[invoice.type]
      },
      110,
    ),
    buildMoneyColumn(PartnerInvoicesTableFields.amount, (invoice) => {
      return invoice.amount
    }),
    buildMoneyColumn(PartnerInvoicesTableFields.paidAmount, (invoice) => {
      return invoice.paidAmount
    }),
    buildDateColumn(PartnerInvoicesTableFields.invoiceDate, 'invoiceDate'),
    buildDateColumn(PartnerInvoicesTableFields.dueDate, 'dueDate'),
    {
      field: PartnerInvoicesTableFields.earningsCount,
      headerName: partnerInvoiceTableHeaderTitles[PartnerInvoicesTableFields.earningsCount],
      width: 110,
      sortable: true,
      type: 'number',
      align: 'right',
      headerAlign: 'right',
    },
    {
      field: PartnerInvoicesTableFields.actions,
      headerName: partnerInvoiceTableHeaderTitles[PartnerInvoicesTableFields.actions],
      width: 68,
      sortable: false,
      hideable: false,
      disableExport: true,
      align: 'right',
      headerAlign: 'right',
      display: 'flex',
      renderCell: (params: GridRenderCellParams<PartnerInvoice>) => {
        return (
          <PartnerInvoiceActionsCell
            invoice={params.row}
            composedInvoiceLabel={composedInvoiceLabel}
            onRunAction={onRunAction}
          />
        )
      },
    },

    // Default-hidden, in the order listed in PARTNER_INVOICE_DEFAULT_HIDDEN_FIELDS.
    buildTextColumn(PartnerInvoicesTableFields.externalStatus, (invoice) => {
      return invoice.externalStatus
    }),
    buildTextColumn(PartnerInvoicesTableFields.loanFundingStatus, (invoice) => {
      return invoice.loanFundingStatus
    }),
    buildTextColumn(
      PartnerInvoicesTableFields.loanFundingErrorMessage,
      (invoice) => {
        return invoice.loanFundingErrorMessage
      },
      240,
    ),
    buildTextColumn(PartnerInvoicesTableFields.paymentStatus, (invoice) => {
      return invoice.paymentStatus
    }),
    buildTextColumn(
      PartnerInvoicesTableFields.paymentErrorMessage,
      (invoice) => {
        return invoice.paymentErrorMessage
      },
      240,
    ),
    buildDateColumn(PartnerInvoicesTableFields.paymentDate, 'paymentDate'),
    buildDateColumn(PartnerInvoicesTableFields.payInvoiceOnDate, 'payInvoiceOnDate'),
    buildTextColumn(
      PartnerInvoicesTableFields.loanId,
      (invoice) => {
        return invoice.loanId
      },
      240,
    ),
    buildDateColumn(PartnerInvoicesTableFields.loanDueDate, 'loanDueDate'),
    buildMoneyColumn(PartnerInvoicesTableFields.loanTotalAmount, (invoice) => {
      return invoice.loanTotalAmount
    }),
    buildMoneyColumn(PartnerInvoicesTableFields.loanBaseFeeAmount, (invoice) => {
      return invoice.loanBaseFeeAmount
    }),
    buildMoneyColumn(PartnerInvoicesTableFields.loanTransferFee, (invoice) => {
      return invoice.loanTransferFee
    }),
    buildRateColumn(PartnerInvoicesTableFields.loanTransferRate, (invoice) => {
      return invoice.loanTransferRate
    }),
    buildMoneyColumn(PartnerInvoicesTableFields.wireTransferFee, (invoice) => {
      return invoice.wireTransferFee
    }),
    buildRateColumn(PartnerInvoicesTableFields.wireTransferRate, (invoice) => {
      return invoice.wireTransferRate
    }),
    buildMoneyColumn(PartnerInvoicesTableFields.gigsafeTotalFeeAmount, (invoice) => {
      return invoice.gigsafeTotalFeeAmount
    }),
    buildTextColumn(
      PartnerInvoicesTableFields.invoiceApprovedByPayor,
      (invoice) => {
        return invoice.invoiceApprovedByPayor ? 'Yes' : 'No'
      },
      150,
    ),
    buildTextColumn(
      PartnerInvoicesTableFields.payorExternalId,
      (invoice) => {
        return invoice.payorExternalId
      },
      240,
    ),
    buildTextColumn(
      PartnerInvoicesTableFields.payeeExternalId,
      (invoice) => {
        return invoice.payeeExternalId
      },
      240,
    ),
    buildTextColumn(
      PartnerInvoicesTableFields.paymentId,
      (invoice) => {
        return invoice.paymentId
      },
      240,
    ),
    buildTextColumn(
      PartnerInvoicesTableFields.description,
      (invoice) => {
        return invoice.description
      },
      280,
    ),
  ]

  if (canManage) {
    return columns
  }

  return columns.filter((column) => {
    return column.field !== PartnerInvoicesTableFields.actions
  })
}
